"use client";

import Image, { ImageProps } from "next/image";
import { useState } from "react";
import { cn } from "@/lib/utils";

type ImageWithFallbackProps = Omit<ImageProps, "onError"> & {
  fallbackClassName?: string;
};

export function ImageWithFallback({
  src,
  alt,
  className,
  fallbackClassName,
  ...props
}: ImageWithFallbackProps) {
  // Track both error state and the src that caused it.
  // If src changes, we treat it as a fresh image (no error).
  const [errorSrc, setErrorSrc] = useState<ImageProps["src"] | null>(null);

  const hasError = errorSrc !== null && errorSrc === src;

  if (hasError) {
    const isDecorative = alt === "";
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-[var(--color-primary)] text-white",
          fallbackClassName ?? className,
        )}
        role={isDecorative ? "presentation" : "img"}
        aria-label={isDecorative ? undefined : (alt as string)}
      >
        <span className="text-4xl" aria-hidden="true">
          🎃
        </span>
      </div>
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      className={className}
      onError={() => setErrorSrc(src)}
      {...props}
    />
  );
}
